import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import dayjs from "dayjs";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faCalendarDays,
  faArrowsRotate,
  faRotateRight,
  faCalendarPlus,
} from "@fortawesome/pro-solid-svg-icons";
import { faXmark } from "@fortawesome/pro-light-svg-icons";
import {
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  Radio,
  RadioGroup,
  FormControlLabel,
  FormGroup,
  IconButton,
  Typography,
  Select,
  MenuItem,
  Slider,
  InputLabel,
} from "@mui/material";
import { DateRangeCalendar, DateRangePickerDay } from "@mui/x-date-pickers-pro";

import * as f from "../../utils/formatter";
import * as api from "../../services/api";
import ButtonLoader from "../ButtonLoader";
import { useFormAction } from "../../form";
import { useCurrentLocation } from "../LocationSelector";
import { toDateString } from "shared/src/date.mjs";
import { useLatest } from "../../hooks";
import Divider from "@mui/material/Divider";
import MonthSelector from "./MonthSelector";
import { indexBy } from "shared/src/util.mjs";
import {
  getTimePartOptions,
  getStartDateFromNote,
  stablePaginateSuggestions,
} from "./utils";
import DoctorsInput from "./DoctorsInput";
import DoctorIcon from "./DoctorIcon";

const unavailableReasons = {
  weekend: "weekend",
  holiday: "svátek",
  blocked: "blokace",
  unavailable_employee: "zaměstnanec nedostupný",
  cannot_fit: "nevejde se",
  past: "minulost",
  doctor_unavailable: "doktor nedostupný",
};

function CustomDateRangePickerDay(props) {
  const { unavailableDays, ...otherProps } = props;

  const extraProps = {};
  if (props.disabled) {
    const day = toDateString(props.day.$d);
    const reason = unavailableReasons[unavailableDays[day]?.reason];

    extraProps.title = reason;
  }

  return <DateRangePickerDay {...extraProps} {...otherProps} />;
}

function useAvailableDays(serviceOption, locationOption, dateRange) {
  const [state, setState] = React.useState({
    availableDays: new Set([]),
    availableDaysLoading: false,
    unavailableDays: {},
  });
  const [from, to] = dateRange;
  const filter = React.useMemo(() => {
    if (!serviceOption?.id || !locationOption?.id) return null;

    const to2 = new Date(to);
    to2.setDate(to2.getDate() + 1);

    return {
      from,
      to: toDateString(to2),
      services: [
        { service_id: serviceOption.id, duration: serviceOption.duration },
      ],
      location_id: locationOption.id,
      group_size: 1,
    };
  }, [serviceOption?.id, locationOption?.id, from, to]);

  useLatest(
    async (signal) => {
      if (!filter) {
        setState({
          availableDays: new Set([]),
          availableDaysLoading: false,
          unavailableDays: {},
        });
        return;
      }
      setState((state) => ({ ...state, availableDaysLoading: true }));
      try {
        const result = await api.appointmentSuggestionsDays(
          { filter },
          { signal },
        );
        setState((state) => ({
          ...state,
          availableDays: new Set(result.data.appointmentSuggestionsDays.days),
          unavailableDays: indexBy(
            result.data.appointmentSuggestionsDays.unavailableDays,
            ({ day }) => day,
          ),
        }));
      } finally {
        if (!signal.aborted) {
          setState((state) => ({ ...state, availableDaysLoading: false }));
        }
      }
    },
    [filter],
  );
  return state;
}

// Helper function to set start of month and end of next month
const setStartAndEndOfMonth = (month) => {
  const startOfMonth = dayjs(month).startOf("month");
  const endOfNextMonth = startOfMonth.add(1, "month").endOf("month");
  return [
    f.dateInput(startOfMonth.toDate()),
    f.dateInput(endOfNextMonth.toDate()),
  ];
};

/**
 * Manual date/time selection dialog.
 * @param {Object} props
 * @param {function} props.close
 * @param {any} props.clientState
 * @param {Array=} props.selectedClients
 * @param {function=} props.setSelectedClients
 * @param {object} props.reservationSettingState
 * @param {function} props.setReservationSettingState
 */
export const ManualDateSelectionDialog = ({
  close,
  cancel,
  clientState,
  selectedClients = [],
  setSelectedClients,
  reservationSettingState,
  setReservationSettingState,
  doctorOptions,
}) => {
  const { t } = useTranslation();
  const locationId = useCurrentLocation();
  const [timeOptions, _] = useState([
    "06:00",
    "07:00",
    "08:00",
    "09:00",
    "10:00",
    "11:00",
    "12:00",
    "13:00",
    "14:00",
    "15:00",
    "16:00",
    "17:00",
    "18:00",
    "19:00",
    "20:00",
    "21:00",
    "22:00",
  ]);

  const index = 0;

  const setData = React.useCallback(
    (index, m) => {
      setSelectedClients((prev) => {
        const updatedClients = [...prev];
        const client = { ...updatedClients[index] };
        updatedClients[index] = client;

        for (const [k, v] of Object.entries(m)) {
          if (typeof v === "function") {
            client[k] = v(client[k]);
          } else {
            client[k] = v;
          }
        }

        return updatedClients;
      });
    },
    [setSelectedClients],
  );

  const data = selectedClients[index];
  const selectedClient = data.selectedClient;
  const setSelectedClient = (val) => setData(index, { selectedClient: val });
  const selectedDoctors = data.selectedDoctors;
  const selectedService = data.selectedService;
  const setSelectedDoctors = (val) => setData(index, { selectedDoctors: val });

  // Primary selection (first client) fallback to legacy state
  const primary = selectedClients[0] || {};
  const activeClient = clientState.client || primary.selectedClient;
  const activeService = primary.selectedService || clientState.serviceOption;
  const activeLocation = primary.selectedLocation || clientState.locationOption;

  const [renderedMonths, setRenderedMonths] = useState(
    setStartAndEndOfMonth(new Date()),
  );
  const { availableDays, unavailableDays, availableDaysLoading } =
    useAvailableDays(activeService, activeLocation, renderedMonths);

  const [quickTimeSelection, setQuickTimeSelection] = useState(
    clientState.note ? "nurseRecommendation" : "closest",
  );
  const [sliderValue, setSliderValue] = useState([0, 16]);

  // Define day part time ranges mapping checkboxes to time ranges
  const dayPartTimes = [
    { from: "06:00", to: "10:00" }, // Ráno
    { from: "09:00", to: "12:00" }, // Dopoledne
    { from: "11:00", to: "13:00" }, // V poledne
    { from: "12:00", to: "17:00" }, // Odpoledne
    { from: "16:00", to: "22:00" }, // Večer
  ];

  // Helper function to convert time string to minutes for comparison
  const timeToMinutes = (time) => {
    if (!time) return 0;
    const [hours, minutes] = time.split(":").map(Number);
    return hours * 60 + minutes;
  };

  // Function to find the closest time option index
  const findClosestTimeIndex = (timeStr) => {
    if (!timeOptions || timeOptions.length === 0) return 0;

    const index = timeOptions.findIndex((time) => time === timeStr);
    if (index !== -1) return index;

    // If exact match not found, find closest time
    const timeMinutes = timeToMinutes(timeStr);
    let closestIndex = 0;
    let minDiff = Number.MAX_SAFE_INTEGER;

    timeOptions.forEach((time, idx) => {
      const diff = Math.abs(timeToMinutes(time) - timeMinutes);
      if (diff < minDiff) {
        minDiff = diff;
        closestIndex = idx;
      }
    });

    return closestIndex;
  };

  useEffect(() => {
    setReservationSettingState((prev) => ({
      ...prev,
      suggestion: selectedClients?.[0]?.selectedSuggestion || null,
    }));
  }, []);

  useEffect(() => {
    const fetchHours = async () => {
      try {
        // If we have existing custom time values, use those to initialize the slider
        if (
          reservationSettingState.customTimeFrom &&
          reservationSettingState.customTimeTo
        ) {
          const fromIndex = findClosestTimeIndex(
            reservationSettingState.customTimeFrom,
          );
          const toIndex = findClosestTimeIndex(
            reservationSettingState.customTimeTo,
          );
          setSliderValue([fromIndex, toIndex]);
        } else {
          // Otherwise initialize with full range
          setSliderValue([0, timeOptions.length - 1]);

          // Only update state if we don't already have values
          setReservationSettingState((prev) => ({
            ...prev,
            customTimeFrom: timeOptions[0],
            customTimeTo: timeOptions[timeOptions.length - 1],
          }));
        }
      } catch (error) {
        console.error("Failed to load working hours:", error);
      }
    };

    if (locationId) {
      fetchHours();
    }
  }, [setReservationSettingState]);

  const { note } = clientState;
  const { oldAppointment } = reservationSettingState;

  const { selectedWeekdays, selectedTimeParts, calendarRange } =
    reservationSettingState;

  const selectionWeeks = {
    oneWeek: 1,
    twoWeeks: 2,
    oneMonth: 4,
    twoMonths: 8,
    threeMonths: 12,
  };

  useEffect(() => {
    const weeks = selectionWeeks[quickTimeSelection];
    let start;
    let end;
    if (quickTimeSelection !== "") {
      if (weeks != null) {
        start = dayjs().add(7 * weeks, "day");
        end = start.clone().add(7, "day");
      } else {
        if (
          note &&
          !!note.reservation_count &&
          quickTimeSelection !== "closest"
        ) {
          start = getStartDateFromNote(note);
          end = start.clone().add(7, "day");
        } else {
          start = dayjs();
          end = start.clone().add(7, "day");
        }
      }
      setReservationSettingState((prev) => ({
        ...prev,
        calendarRange: [start, end],
      }));
    }
  }, [quickTimeSelection, note, setReservationSettingState]);

  const { loading, onSubmit } = useFormAction(
    async () => {
      setReservationSettingState((prev) => ({
        ...prev,
        suggestion: reservationSettingState.suggestion,
        isDateManuallySelected: true,
      }));
    },
    () => close(),
  );

  // Deterministic, shared ordering with parent
  const pages = React.useMemo(
    () =>
      stablePaginateSuggestions(reservationSettingState.suggestions || [], 5),
    [reservationSettingState.suggestions],
  );
  const pageIndex = reservationSettingState.primarySuggestionsPage || 0;
  const currentSuggestions = pages[pageIndex] || [];
  const canGoBackEnabled = pageIndex > 0;
  const hasMoreEnabled = pageIndex < pages.length - 1;
  const handleNextSuggestions = () => {
    if (!hasMoreEnabled) return;
    setReservationSettingState((s) => ({
      ...s,
      primarySuggestionsPage: pageIndex + 1,
    }));
  };
  const handleBackSuggestions = () => {
    if (!canGoBackEnabled) return;
    setReservationSettingState((s) => ({
      ...s,
      primarySuggestionsPage: pageIndex - 1,
    }));
  };

  const submitDisabled = loading;

  // Update slider when checkbox selection changes
  useEffect(() => {
    const selectedCount = selectedTimeParts.filter(Boolean).length;

    if (selectedCount === 1) {
      // Find which checkbox is selected
      const selectedIndex = selectedTimeParts.findIndex(Boolean);
      const { from, to } = dayPartTimes[selectedIndex];

      // Find closest indices in timeOptions
      const fromIndex = findClosestTimeIndex(from);
      const toIndex = findClosestTimeIndex(to);

      // Update slider
      setSliderValue([fromIndex, toIndex]);

      // Also update custom time range in state
      setReservationSettingState((prev) => ({
        ...prev,
        customTimeFrom: timeOptions[fromIndex] || from,
        customTimeTo: timeOptions[toIndex] || to,
      }));
    } else if (selectedCount > 1) {
      // Multiple checkboxes selected - disable slider
      setReservationSettingState((prev) => ({
        ...prev,
        customTimeFrom: null,
        customTimeTo: null,
      }));
    } else if (selectedCount === 0 && timeOptions.length > 0) {
      // No checkboxes selected - restore slider values to state
      setSliderValue([0, timeOptions.length - 1]);
      setReservationSettingState((prev) => ({
        ...prev,
        customTimeFrom: timeOptions[0],
        customTimeTo: timeOptions[timeOptions.length - 1],
      }));
    }
  }, [selectedTimeParts, timeOptions, setReservationSettingState]);

  return (
    <Dialog open={true} onClose={() => close()} maxWidth="lg" scroll="paper">
      <DialogTitle>
        {t("calendar.manualTimeSelectionTitle")}
        <IconButton aria-label="close" onClick={() => cancel()}>
          <FontAwesomeIcon icon={faXmark} />
        </IconButton>
      </DialogTitle>

      <DialogContent>
        <form onSubmit={onSubmit} id="manualDateSelectionForm">
          <Box display="flex" flexDirection="row" gap="1rem">
            <Box>
              <Box sx={{ mb: 1, display: "inline-block" }}>
                <Typography variant="h3">Rychlý výběr</Typography>
                <Divider sx={{ mt: 1.5 }} />
              </Box>
              <FormGroup>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={selectedWeekdays[0]}
                      onChange={(e) => {
                        const newSelectedWeekDays = [...selectedWeekdays];
                        newSelectedWeekDays[0] = e.target.checked;
                        setReservationSettingState((prev) => ({
                          ...prev,
                          selectedWeekdays: newSelectedWeekDays,
                        }));
                      }}
                    />
                  }
                  label="Pondělí"
                  name="0"
                />
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={selectedWeekdays[1]}
                      onChange={(e) => {
                        const newSelectedWeekDays = [...selectedWeekdays];
                        newSelectedWeekDays[1] = e.target.checked;
                        setReservationSettingState((prev) => ({
                          ...prev,
                          selectedWeekdays: newSelectedWeekDays,
                        }));
                      }}
                    />
                  }
                  label="Úterý"
                  name="1"
                />
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={selectedWeekdays[2]}
                      onChange={(e) => {
                        const newSelectedWeekDays = [...selectedWeekdays];
                        newSelectedWeekDays[2] = e.target.checked;
                        setReservationSettingState((prev) => ({
                          ...prev,
                          selectedWeekdays: newSelectedWeekDays,
                        }));
                      }}
                    />
                  }
                  label="Středa"
                  name="2"
                />
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={selectedWeekdays[3]}
                      onChange={(e) => {
                        const newSelectedWeekDays = [...selectedWeekdays];
                        newSelectedWeekDays[3] = e.target.checked;
                        setReservationSettingState((prev) => ({
                          ...prev,
                          selectedWeekdays: newSelectedWeekDays,
                        }));
                      }}
                    />
                  }
                  label="Čtvrtek"
                  name="3"
                />
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={selectedWeekdays[4]}
                      onChange={(e) => {
                        const newSelectedWeekDays = [...selectedWeekdays];
                        newSelectedWeekDays[4] = e.target.checked;
                        setReservationSettingState((prev) => ({
                          ...prev,
                          selectedWeekdays: newSelectedWeekDays,
                        }));
                      }}
                    />
                  }
                  label="Pátek"
                  name="4"
                />
              </FormGroup>

              <FormControl>
                <RadioGroup
                  onChange={(event) =>
                    setQuickTimeSelection(event.target.value)
                  }
                >
                  {note && !!note.reservation_count && (
                    <FormControlLabel
                      value="nurseRecommendation"
                      control={
                        <Radio
                          checked={quickTimeSelection === "nurseRecommendation"}
                        />
                      }
                      label="Doporučení&nbsp;sestry"
                    />
                  )}
                  <FormControlLabel
                    value="closest"
                    control={
                      <Radio checked={quickTimeSelection === "closest"} />
                    }
                    label="Nejbližší&nbsp;termín"
                  />
                  <FormControlLabel
                    value="oneWeek"
                    control={
                      <Radio checked={quickTimeSelection === "oneWeek"} />
                    }
                    label="Za týden"
                  />
                  <FormControlLabel
                    value="twoWeeks"
                    control={
                      <Radio checked={quickTimeSelection === "twoWeeks"} />
                    }
                    label="Za 14 dní"
                  />
                  <FormControlLabel
                    value="oneMonth"
                    control={
                      <Radio checked={quickTimeSelection === "oneMonth"} />
                    }
                    label="Za 1 měsíc"
                  />
                  <FormControlLabel
                    value="twoMonths"
                    control={
                      <Radio checked={quickTimeSelection === "twoMonths"} />
                    }
                    label="Za 2 měsíce"
                  />
                  <FormControlLabel
                    value="threeMonths"
                    control={
                      <Radio checked={quickTimeSelection === "threeMonths"} />
                    }
                    label="Za 3 měsíce"
                  />
                </RadioGroup>
              </FormControl>

              <FormGroup>
                {getTimePartOptions().map((option, index) => (
                  <FormControlLabel
                    key={index}
                    control={
                      <Checkbox
                        checked={selectedTimeParts[index]}
                        onChange={(e) => {
                          const newSelectedTimeParts = [...selectedTimeParts];
                          newSelectedTimeParts[index] = e.target.checked;
                          setReservationSettingState((prev) => ({
                            ...prev,
                            selectedTimeParts: newSelectedTimeParts,
                          }));
                        }}
                      />
                    }
                    label={option}
                    name={index.toString()}
                  />
                ))}
              </FormGroup>

              <DoctorsInput
                doctorOptions={doctorOptions}
                selectedDoctors={selectedDoctors}
                selectedService={selectedService}
                setSelectedDoctors={setSelectedDoctors}
              />
              <FormGroup>
                <FormControlLabel
                  disabled={selectedService?.requires_doctor}
                  control={
                    <Checkbox
                      checked={
                        selectedClient?.with_selected_doctor_only ?? false
                      }
                      onChange={(_, checked) => {
                        if (selectedService?.requires_doctor) {
                          return;
                        }

                        setSelectedClient((prev) => ({
                          ...prev,
                          with_selected_doctor_only: checked,
                        }));
                      }}
                    />
                  }
                  label="Pouze termíny s lékařem"
                />
              </FormGroup>
            </Box>
            <Box sx={{ flexGrow: 1 }}>
              {selectedClients.length > 1 && (
                <Box sx={{ mb: 2, display: "inline-block" }}>
                  <Typography variant="h3">Společné objednání</Typography>
                </Box>
              )}
              {selectedClients.length === 1 && (
                <Box>
                  <Box sx={{ mb: 2, display: "inline-block" }}>
                    <Typography variant="h3">
                      Objednat klienta <i>{f.fullName(activeClient)}</i> na:
                    </Typography>
                    <Divider sx={{ mt: 1.5 }} />
                  </Box>

                  <Typography sx={{ mb: 2 }}>
                    <strong>Úkon: </strong> {activeService?.name}
                    {note && (
                      <>
                        {", "}
                        <strong>Období: </strong>
                        {note.reservation_count}{" "}
                        {t(`appointmentNotes.${note.reservation_unit}`)}
                        {", "}
                        {/* TODO sklonovani */}
                        <strong>Délka app.: </strong>
                        {activeService?.duration} minut
                        {", "}
                        <strong>Poznámka pro recepci: </strong>{" "}
                        {note.note_content}
                        {", "}
                        <strong>Poslední app.: </strong>
                        {f.date(oldAppointment?.start)}
                        {" - "}
                        {oldAppointment?.service.name}
                        {", "}
                        <strong>Nutná přítomnost lékaře: </strong>
                        {note.doctor_required ? "Ano" : "Ne"}
                      </>
                    )}
                  </Typography>
                </Box>
              )}
              <Divider sx={{ mb: 2 }} />

              <Box sx={{ display: "flex", flexDirection: "row", gap: "3rem" }}>
                <Box sx={{ mb: 2 }}>
                  <Box
                    sx={{
                      display: "flex",
                      flexDirection: "row",
                      gap: "2rem",
                      alignItems: "flex-end",
                      mb: 2,
                    }}
                  >
                    <FormControl>
                      <InputLabel>Od</InputLabel>
                      <Select
                        disabled={
                          reservationSettingState.selectedTimeParts.filter(
                            (value) => value,
                          ).length > 1
                        }
                        value={sliderValue[0]}
                        onChange={(event) => {
                          const newStartValue = event.target.value;
                          const newSliderValue = [
                            newStartValue,
                            newStartValue > sliderValue[1]
                              ? newStartValue
                              : sliderValue[1],
                          ];
                          setSliderValue(newSliderValue);

                          // Uncheck any selected checkbox when slider is manually changed
                          if (selectedTimeParts.filter(Boolean).length <= 1) {
                            setReservationSettingState((prev) => ({
                              ...prev,
                              selectedTimeParts: [
                                false,
                                false,
                                false,
                                false,
                                false,
                              ],
                              customTimeFrom: timeOptions[newSliderValue[0]],
                              customTimeTo: timeOptions[newSliderValue[1]],
                            }));
                          }
                        }}
                      >
                        {timeOptions?.map((time, index) => (
                          <MenuItem value={index} key={time}>
                            {time}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                    <FormControl>
                      <InputLabel>Do</InputLabel>
                      <Select
                        disabled={
                          reservationSettingState.selectedTimeParts.filter(
                            (value) => value,
                          ).length > 1
                        }
                        value={sliderValue[1]}
                        onChange={(event) => {
                          const newEndValue = event.target.value;
                          setSliderValue([sliderValue[0], newEndValue]);

                          // Uncheck any selected checkbox when slider is manually changed
                          if (selectedTimeParts.filter(Boolean).length <= 1) {
                            setReservationSettingState((prev) => ({
                              ...prev,
                              selectedTimeParts: [
                                false,
                                false,
                                false,
                                false,
                                false,
                              ],
                              customTimeFrom: timeOptions[sliderValue[0]],
                              customTimeTo: timeOptions[newEndValue],
                            }));
                          }
                        }}
                      >
                        {timeOptions?.map((time, index) => (
                          <MenuItem value={index} key={time}>
                            {time}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                    <Box sx={{ flexGrow: 1 }}>
                      <Slider
                        value={sliderValue}
                        onChange={(event, newValue) => {
                          setSliderValue(newValue);

                          // Uncheck any selected checkbox when slider is manually moved
                          if (selectedTimeParts.filter(Boolean).length <= 1) {
                            setReservationSettingState((prev) => ({
                              ...prev,
                              selectedTimeParts: [
                                false,
                                false,
                                false,
                                false,
                                false,
                              ],
                              customTimeFrom: timeOptions[newValue[0]],
                              customTimeTo: timeOptions[newValue[1]],
                            }));
                          }
                        }}
                        valueLabelDisplay="on"
                        valueLabelFormat={(value) => timeOptions[value]}
                        min={0}
                        max={timeOptions.length - 1}
                        disabled={
                          reservationSettingState.selectedTimeParts.filter(
                            (value) => value,
                          ).length > 1
                        }
                      />
                    </Box>
                  </Box>

                  <Box sx={{ mb: 2 }}>
                    <MonthSelector
                      setReservationSettingState={setReservationSettingState}
                      renderedMonths={renderedMonths}
                    />
                    <DateRangeCalendar
                      disablePast
                      value={reservationSettingState.calendarRange}
                      onMonthChange={(month) => {
                        setRenderedMonths(
                          setStartAndEndOfMonth(f.dateInput(month.toDate())),
                        );
                      }}
                      onChange={(newValue) => {
                        setQuickTimeSelection("");
                        setReservationSettingState((prev) => ({
                          ...prev,
                          calendarRange: newValue,
                        }));
                      }}
                      sx={(theme) => ({
                        gap: theme.spacing(4),
                        "& .MuiDateRangeCalendar-monthContainer": {
                          "&:first-child": {
                            borderRadius: 1,
                          },
                          "&:last-child": {
                            borderRadius: 1,
                          },
                        },
                      })}
                      loading={availableDaysLoading}
                      shouldDisableDate={(day) => {
                        return !availableDays.has(toDateString(day.toDate()));
                      }}
                      slots={{
                        day: (p) => (
                          <CustomDateRangePickerDay
                            {...p}
                            unavailableDays={unavailableDays}
                          />
                        ),
                      }}
                    />
                  </Box>
                </Box>

                {calendarRange?.[0] &&
                  calendarRange?.[1] &&
                  calendarRange[0].$d &&
                  calendarRange[1].$d &&
                  dayjs(calendarRange[0].$d).isSame(
                    calendarRange[1].$d,
                    "day",
                  ) && (
                    <Box>
                      <Box>
                        <Box sx={{ display: "inline-block", mb: 2 }}>
                          <Typography variant="h3">
                            Termíny ve vybraný den:
                          </Typography>
                          <Divider sx={{ mt: 1.5 }} />
                        </Box>
                      </Box>
                      <>
                        <FormControl
                          required
                          disabled={reservationSettingState.suggestionsLoading}
                          sx={{ maxHeight: "38rem", overflowY: "scroll" }}
                        >
                          <RadioGroup
                            onChange={(_, value) => {
                              const sel = (
                                reservationSettingState.suggestions || []
                              ).find((s) => s.id === value);
                              setReservationSettingState((prev) => ({
                                ...prev,
                                suggestion: sel,
                              }));
                              if (
                                sel &&
                                typeof setSelectedClients === "function"
                              ) {
                                setSelectedClients((prev) => {
                                  if (!prev.length) return prev;
                                  const next = [...prev];
                                  next[0] = {
                                    ...next[0],
                                    selectedSuggestion: sel,
                                  };
                                  return next;
                                });
                              }
                            }}
                          >
                            {reservationSettingState.suggestions
                              ?.filter((s) =>
                                dayjs(s.start).isSame(
                                  dayjs(calendarRange[0]),
                                  "day",
                                ),
                              )
                              .sort(
                                (a, b) =>
                                  new Date(a.start).getTime() -
                                  new Date(b.start).getTime(),
                              )
                              .map((s) => {
                                const label = <>{f.time24(s.start)}</>;

                                return (
                                  <FormControlLabel
                                    key={s.id}
                                    value={s.id}
                                    control={<Radio />}
                                    label={label}
                                  />
                                );
                              })}
                          </RadioGroup>
                        </FormControl>
                      </>
                    </Box>
                  )}

                {calendarRange?.[0] &&
                  calendarRange?.[1] &&
                  calendarRange[0].$d &&
                  calendarRange[1].$d &&
                  !dayjs(calendarRange[0].$d).isSame(
                    calendarRange[1].$d,
                    "day",
                  ) && (
                    <Box>
                      <Box>
                        <Box sx={{ display: "inline-block", mb: 2 }}>
                          <Typography variant="h3">Volné termíny:</Typography>
                          <Divider sx={{ mt: 1.5 }} />
                        </Box>
                      </Box>

                      <FormControl
                        required
                        disabled={reservationSettingState.suggestionsLoading}
                        sx={{ mb: 2 }}
                      >
                        <RadioGroup
                          value={reservationSettingState.suggestion?.id}
                          onChange={(_, value) => {
                            const sel = currentSuggestions?.find(
                              (s) => s.id === value,
                            );
                            setReservationSettingState((prev) => ({
                              ...prev,
                              suggestion: sel,
                            }));
                            if (
                              sel &&
                              typeof setSelectedClients === "function"
                            ) {
                              setSelectedClients((prev) => {
                                if (!prev.length) return prev;
                                const next = [...prev];
                                next[0] = {
                                  ...next[0],
                                  selectedSuggestion: sel,
                                };
                                return next;
                              });
                            }
                          }}
                        >
                          {currentSuggestions?.map((s) => {
                            const label = (
                              <>
                                {f.longDateTime(s.start)}
                                {note &&
                                  !!note.reservation_count &&
                                  s.generationRound > 1 && (
                                    <>
                                      {" "}
                                      -{" "}
                                      <FontAwesomeIcon icon={faCalendarPlus} />
                                    </>
                                  )}
                                <DoctorIcon
                                  selectedDoctors={selectedDoctors}
                                  availableEmployees={s.availableEmployees}
                                />
                              </>
                            );

                            return (
                              <FormControlLabel
                                key={s.id}
                                value={s.id}
                                control={<Radio />}
                                label={label}
                              />
                            );
                          })}
                        </RadioGroup>
                      </FormControl>
                      <Box
                        sx={{
                          display: "flex",
                          flexDirection: "row",
                          gap: "2rem",
                        }}
                      >
                        <Button
                          sx={{ padding: "0.7rem 2.4rem", gap: "0.6rem" }}
                          disabled={!canGoBackEnabled}
                          onClick={() => handleBackSuggestions()}
                          variant="outlined"
                        >
                          <FontAwesomeIcon icon={faRotateRight} />
                          {t("form.back")}
                        </Button>
                        <Button
                          sx={{ padding: "0.7rem 2.4rem", gap: "0.6rem" }}
                          disabled={!hasMoreEnabled}
                          onClick={() => handleNextSuggestions()}
                          variant="outlined"
                        >
                          <FontAwesomeIcon icon={faArrowsRotate} />
                          {t("form.next")}
                        </Button>
                      </Box>
                    </Box>
                  )}
              </Box>

              <Box sx={{ display: "inline-block", mb: 2 }}>
                <Typography variant="h3">Vybraný čas:</Typography>
                <Divider sx={{ mt: 1.5 }} />
              </Box>
              <Typography
                variant="h4"
                sx={{ color: "primary.main", minHeight: "1.6rem" }}
              >
                {f.longDateTime(
                  new Date(reservationSettingState.suggestion?.start),
                )}
              </Typography>
            </Box>
          </Box>
        </form>
      </DialogContent>
      <DialogActions>
        <Button onClick={() => cancel()} variant="outlined">
          {t("form.cancel")}
        </Button>
        <Button
          type="submit"
          disabled={submitDisabled || !reservationSettingState.suggestion}
          sx={{ padding: "0.7rem 2.4rem", gap: "0.6rem" }}
          variant="contained"
          form="manualDateSelectionForm"
        >
          {loading && <ButtonLoader />}
          <FontAwesomeIcon icon={faCalendarDays} />
          {t("form.continue")}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
